import { printError, useUtilities } from "helpers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  createChecklistTemplate,
  editChecklistTemplate,
  removeChecklistTemplate,
  setTemplateChecklists,
  setDefaultChecklists,
  addToDefaultChecklists,
  removeFromDefaultChecklists,
} from "redux/reducers/templateLibrary/checklists";
import { ApiResponseType, ModulePrefixes, NotificationType } from "enums";
import {
  defaultTaskAdd,
  defaultTaskRemove,
  defaultTaskShow,
  templateTaskAdd,
  templateTaskDelete,
  templateTaskEdit,
  templateTaskShow,
} from "./modularApi";
import { invalidateTemplatePhases } from "./phases";
import { t } from "i18next";

export function useGetChecklistTemplates() {
  return useQuery(["checklistTemplates"], () => templateTaskShow("checklist"));
}

export const useCheckListTemplates = () => {
  const { data, isLoading, error } = useGetChecklistTemplates();
  const {
    checklists: { checklists, loaded },
    dispatch,
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) {
      dispatch(setTemplateChecklists(data.data));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, loaded]);

  return { data: checklists, isLoading: isLoading || !loaded, error };
};

export function useAddChecklistTemplate(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskAdd({
        ...params,
        parent_type: "checklist",
        module_hint: "checklist",
      }),
    {
      onSuccess: (data) => {
        if (data.status_code === ApiResponseType.SUCCESS) {
          dispatch(createChecklistTemplate(data.data));
          notification({
            type: NotificationType.SUCCESS,
            message: t("libraryTemplate.checklist.api.success.added"),
          });
          if (redirect && data?.data?.id) navigate(redirect + data.data.id);
          dispatch(invalidateTemplatePhases());
        } else {
          notification({
            type: NotificationType.ERROR,
            message: printError(data.message),
          });
        }
      },
      onError: (error) => {
        console.log(error);
        notification({
          type: NotificationType.ERROR,
          message: printError(error.message),
        });
      },
    }
  );
}

export function useUpdateChecklistTemplate() {
  const { dispatch, notification } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskEdit({
        ...params,
        parent_type: "checklist",
        module_hint: "checklist",
      }),
    {
      onSuccess: (data) => {
        if (data.status_code === ApiResponseType.SUCCESS) {
          dispatch(editChecklistTemplate(data.data));
          notification({
            type: NotificationType.SUCCESS,
            message: t("libraryTemplate.checklist.api.success.updated"),
          });
          dispatch(invalidateTemplatePhases());
        } else {
          notification({
            type: NotificationType.ERROR,
            message: printError(data.message),
          });
        }
      },
      onError: (error) => {
        console.log(error);
        notification({
          type: NotificationType.ERROR,
          message: printError(error.message),
        });
      },
    }
  );
}

export function useDeleteChecklistTemplate() {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(templateTaskDelete, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(removeChecklistTemplate(data.id));
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.checklist.api.success.deleted"),
        });
        navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists`);
        dispatch(invalidateTemplatePhases());
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log(error);
      notification({
        type: NotificationType.ERROR,
        message: printError(error.message),
      });
    },
  });
}

export function useGetChecklistDefaultTemplates() {
  return useQuery(["checklistDefaultTemplates"], () =>
    defaultTaskShow("checklist")
  );
}

export const useChecklistDefaultTemplates = () => {
  const { data, isLoading, error, isRefetching } = useGetChecklistDefaultTemplates();
  const {
    checklists: {
      defaultSite: { checklists, loaded },
    },
    dispatch,
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (data) {
      dispatch(setDefaultChecklists(data.data));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, loaded, isRefetching]);

  return { data: checklists, isLoading: isLoading || !loaded, error };
};

export function useAddChecklistToDefaultTemplate() {
  const { dispatch, notification } = useUtilities();
  return useMutation(defaultTaskAdd, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(addToDefaultChecklists(data.data));
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.checklist.api.success.addedToSiteTemplate"),
        });
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log(error);
      notification({
        type: NotificationType.ERROR,
        message: printError(error.message),
      });
    },
  });
}

export function useRemoveChecklistDefault() {
  const { dispatch, notification } = useUtilities();
  return useMutation(defaultTaskRemove, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(removeFromDefaultChecklists(data.id));
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.checklist.api.success.removedFromSiteTemplate"),
        });
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      notification({
        type: NotificationType.ERROR,
        message: printError(error.message),
      });
    },
  });
}
